/**
 * Landing pages (migration 00034, docs/LANDING_PAGES.md). Pure: no React, no
 * data access. Unit tested in src/test/landingTypes.test.ts.
 *
 * A page is a header plus sections. Each section picks a column layout (one
 * to four columns, equal or weighted) and holds blocks per column: forms,
 * links, text, the viewer's work counts, and their recent requests. The page
 * is edited as a draft and published; viewers always read the published
 * layout. Forms are referenced by family (draftId, else the form id), so a
 * republished form stays on the page and opens its latest live version.
 */
import type { PublishedForm } from '@/types/form-spec'
import type { Visibility } from '@/services/reports/reportDefinition'

// ------------------------------------------------------------------ columns
export type ColumnPreset = '1' | '1-1' | '1-1-1' | '1-1-1-1' | '2-1' | '1-2' | '1-2-1' | '3-1' | '1-3'

export interface ColumnPresetInfo {
  value: ColumnPreset
  label: string
  /** Relative widths, left to right. */
  weights: number[]
}

export const COLUMN_PRESETS: ColumnPresetInfo[] = [
  { value: '1', label: 'One column', weights: [1] },
  { value: '1-1', label: 'Two equal columns', weights: [1, 1] },
  { value: '1-1-1', label: 'Three equal columns', weights: [1, 1, 1] },
  { value: '1-1-1-1', label: 'Four equal columns', weights: [1, 1, 1, 1] },
  { value: '2-1', label: 'Wide left, narrow right', weights: [2, 1] },
  { value: '1-2', label: 'Narrow left, wide right', weights: [1, 2] },
  { value: '3-1', label: 'Three quarters, one quarter', weights: [3, 1] },
  { value: '1-3', label: 'One quarter, three quarters', weights: [1, 3] },
  { value: '1-2-1', label: 'Wide middle', weights: [1, 2, 1] },
]

export function presetInfo(preset: ColumnPreset): ColumnPresetInfo {
  return COLUMN_PRESETS.find((p) => p.value === preset) ?? COLUMN_PRESETS[0]!
}

export function columnCount(preset: ColumnPreset): number {
  return presetInfo(preset).weights.length
}

/** CSS grid-template-columns for the preset (used from the md breakpoint up). */
export function gridTemplate(preset: ColumnPreset): string {
  return presetInfo(preset).weights.map((w) => `minmax(0, ${w}fr)`).join(' ')
}

// ------------------------------------------------------------------- blocks
export type CardSize = 'compact' | 'comfortable' | 'large'
export type Tone = 'plain' | 'info' | 'success' | 'warning'
export type SectionBackground = 'none' | 'white' | 'tint' | 'brand'

export type LinkTarget =
  | { type: 'app'; path: string }
  | { type: 'url'; url: string; newTab?: boolean }
  | { type: 'form'; formFamily: string }
  | { type: 'page'; slug: string }

export interface LinkItem {
  id: string
  label: string
  description?: string
  icon?: string
  target: LinkTarget
}

export type FormsSource =
  | { mode: 'pick'; formFamilies: string[] }
  | { mode: 'surface'; surface: string }
  | { mode: 'all' }

export interface FormsBlock {
  id: string
  kind: 'forms'
  title?: string
  description?: string
  source: FormsSource
  display: 'cards' | 'tiles' | 'list'
  size: CardSize
  showDescription: boolean
  showNext: boolean
}

export interface LinksBlock {
  id: string
  kind: 'links'
  title?: string
  description?: string
  items: LinkItem[]
  display: 'tiles' | 'list' | 'buttons'
  size: CardSize
}

export interface TextBlock {
  id: string
  kind: 'text'
  title?: string
  body: string
  tone: Tone
}

export interface MyWorkBlock {
  id: string
  kind: 'my-work'
  title?: string
}

export interface RecentRequestsBlock {
  id: string
  kind: 'recent-requests'
  title?: string
  limit: number
}

export type LandingBlock = FormsBlock | LinksBlock | TextBlock | MyWorkBlock | RecentRequestsBlock
export type BlockKind = LandingBlock['kind']

export interface BlockKindInfo {
  kind: BlockKind
  label: string
  description: string
  icon: string
}

export const BLOCK_KINDS: BlockKindInfo[] = [
  { kind: 'forms', label: 'Forms', description: 'Forms people can start, picked one by one or by where they apply.', icon: 'file-input' },
  { kind: 'links', label: 'Links', description: 'Shortcuts to pages in the app, other landing pages, or websites.', icon: 'link' },
  { kind: 'text', label: 'Text', description: 'A heading and a few lines: instructions, a notice or contacts.', icon: 'type' },
  { kind: 'my-work', label: 'My work', description: 'Approvals waiting on the viewer and their open requests.', icon: 'clipboard-check' },
  { kind: 'recent-requests', label: 'Recent requests', description: "The viewer's latest requests and where each one sits.", icon: 'inbox' },
]

export function blockKindInfo(kind: BlockKind): BlockKindInfo {
  return BLOCK_KINDS.find((k) => k.kind === kind)!
}

// ------------------------------------------------------------ page + layout
export interface LandingHeader {
  /** Shown above the page; blank uses the page name. */
  title?: string
  subtitle?: string
  style: 'simple' | 'banner'
  /** A box that filters every form and link on the page as the viewer types. */
  showSearch: boolean
}

export interface LandingSection {
  id: string
  title?: string
  description?: string
  columns: ColumnPreset
  background: SectionBackground
  /** One list of blocks per column of the preset. */
  blocks: LandingBlock[][]
}

export interface LandingLayout {
  header: LandingHeader
  sections: LandingSection[]
}

export interface LandingPage {
  id: string
  name: string
  slug: string
  description: string | null
  icon: string | null
  layout: LandingLayout
  draft: LandingLayout | null
  publishedAt: string | null
  publishedBy: string | null
  visibility: Visibility
  sharePrincipals: string[]
  homeFor: string[]
  homePriority: number
  showInNav: boolean
  navOrder: number
  systemKey: string | null
  ownerEmail: string | null
  ownerName: string | null
  updatedAt: string
}

export const MAX_SECTIONS = 40
export const MAX_BLOCKS_PER_COLUMN = 20
export const MAX_LINKS_PER_BLOCK = 40

// -------------------------------------------------------------- factories
export function newId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`
}

export function emptyHeader(): LandingHeader {
  return { title: '', subtitle: '', style: 'banner', showSearch: true }
}

export function emptyLayout(): LandingLayout {
  return { header: emptyHeader(), sections: [newSection('1-1')] }
}

export function newSection(columns: ColumnPreset = '1-1'): LandingSection {
  return { id: newId('sec'), title: '', description: '', columns, background: 'none', blocks: Array.from({ length: columnCount(columns) }, () => []) }
}

export function newBlock(kind: BlockKind): LandingBlock {
  switch (kind) {
    case 'forms': return { id: newId('blk'), kind, title: 'Requests', description: '', source: { mode: 'pick', formFamilies: [] }, display: 'cards', size: 'comfortable', showDescription: true, showNext: false }
    case 'links': return { id: newId('blk'), kind, title: 'Quick links', description: '', items: [], display: 'list', size: 'comfortable' }
    case 'text': return { id: newId('blk'), kind, title: 'Welcome', body: '', tone: 'plain' }
    case 'my-work': return { id: newId('blk'), kind, title: 'My work' }
    case 'recent-requests': return { id: newId('blk'), kind, title: 'My recent requests', limit: 5 }
  }
}

/** A deep copy with fresh ids, for Duplicate. */
export function cloneBlock(block: LandingBlock): LandingBlock {
  const copy = JSON.parse(JSON.stringify(block)) as LandingBlock
  copy.id = newId('blk')
  if (copy.kind === 'links') copy.items = copy.items.map((i) => ({ ...i, id: newId('lnk') }))
  return copy
}

export function cloneSection(section: LandingSection): LandingSection {
  return { ...JSON.parse(JSON.stringify(section)) as LandingSection, id: newId('sec'), blocks: section.blocks.map((col) => col.map(cloneBlock)) }
}

// ------------------------------------------------------------ normalizing
const PRESETS = new Set(COLUMN_PRESETS.map((p) => p.value))
const SIZES = new Set<CardSize>(['compact', 'comfortable', 'large'])
const TONES = new Set<Tone>(['plain', 'info', 'success', 'warning'])
const BACKGROUNDS = new Set<SectionBackground>(['none', 'white', 'tint', 'brand'])

const str = (v: unknown, max = 2000): string => (typeof v === 'string' ? v.slice(0, max) : '')
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {})

function normalizeTarget(raw: unknown): LinkTarget | null {
  const t = obj(raw)
  if (t.type === 'app' && typeof t.path === 'string' && t.path.startsWith('/')) return { type: 'app', path: t.path }
  if (t.type === 'url' && typeof t.url === 'string') return { type: 'url', url: t.url, newTab: t.newTab !== false }
  if (t.type === 'form' && typeof t.formFamily === 'string' && t.formFamily) return { type: 'form', formFamily: t.formFamily }
  if (t.type === 'page' && typeof t.slug === 'string' && t.slug) return { type: 'page', slug: t.slug }
  return null
}

function normalizeBlock(raw: unknown): LandingBlock | null {
  const b = obj(raw)
  const id = str(b.id, 40) || newId('blk')
  const title = str(b.title, 160)
  switch (b.kind) {
    case 'forms': {
      const s = obj(b.source)
      const source: FormsSource = s.mode === 'surface' && typeof s.surface === 'string' ? { mode: 'surface', surface: s.surface }
        : s.mode === 'all' ? { mode: 'all' }
        : { mode: 'pick', formFamilies: Array.isArray(s.formFamilies) ? s.formFamilies.filter((f): f is string => typeof f === 'string') : [] }
      return {
        id, kind: 'forms', title, description: str(b.description), source,
        display: b.display === 'tiles' || b.display === 'list' ? b.display : 'cards',
        size: SIZES.has(b.size as CardSize) ? (b.size as CardSize) : 'comfortable',
        showDescription: b.showDescription !== false, showNext: b.showNext === true,
      }
    }
    case 'links': {
      const items = (Array.isArray(b.items) ? b.items : []).slice(0, MAX_LINKS_PER_BLOCK).flatMap((r): LinkItem[] => {
        const i = obj(r)
        const target = normalizeTarget(i.target)
        if (!target) return []
        return [{ id: str(i.id, 40) || newId('lnk'), label: str(i.label, 120), description: str(i.description, 300), icon: str(i.icon, 40) || undefined, target }]
      })
      return { id, kind: 'links', title, description: str(b.description), items, display: b.display === 'tiles' || b.display === 'buttons' ? b.display : 'list', size: SIZES.has(b.size as CardSize) ? (b.size as CardSize) : 'comfortable' }
    }
    case 'text':
      return { id, kind: 'text', title, body: str(b.body, 6000), tone: TONES.has(b.tone as Tone) ? (b.tone as Tone) : 'plain' }
    case 'my-work':
      return { id, kind: 'my-work', title }
    case 'recent-requests': {
      const n = Number(b.limit)
      return { id, kind: 'recent-requests', title, limit: Number.isFinite(n) ? Math.min(20, Math.max(1, Math.round(n))) : 5 }
    }
    default:
      return null
  }
}

/** Read a stored layout defensively: unknown blocks are dropped and each
 *  section has exactly one block list per column of its preset. */
export function normalizeLayout(raw: unknown): LandingLayout {
  const l = obj(raw)
  const h = obj(l.header)
  const header: LandingHeader = {
    title: str(h.title, 160), subtitle: str(h.subtitle, 600),
    style: h.style === 'simple' ? 'simple' : 'banner',
    showSearch: h.showSearch !== false,
  }
  const sections = (Array.isArray(l.sections) ? l.sections : []).slice(0, MAX_SECTIONS).map((r): LandingSection => {
    const s = obj(r)
    const columns: ColumnPreset = PRESETS.has(s.columns as ColumnPreset) ? (s.columns as ColumnPreset) : '1'
    const rawCols = Array.isArray(s.blocks) ? s.blocks : []
    const cols = rawCols.map((c) => (Array.isArray(c) ? c.map(normalizeBlock).filter((x): x is LandingBlock => !!x) : []))
    return fitColumns({
      id: str(s.id, 40) || newId('sec'), title: str(s.title, 160), description: str(s.description, 600), columns,
      background: BACKGROUNDS.has(s.background as SectionBackground) ? (s.background as SectionBackground) : 'none',
      blocks: cols,
    })
  })
  return { header, sections }
}

/** Make a section's block lists match its preset. Blocks of columns that no
 *  longer exist move to the end of the last column, so nothing is lost. */
export function fitColumns(section: LandingSection): LandingSection {
  const n = columnCount(section.columns)
  const blocks = section.blocks.slice(0, n).map((c) => [...c])
  while (blocks.length < n) blocks.push([])
  const overflow = section.blocks.slice(n).flat()
  if (overflow.length) blocks[n - 1] = [...blocks[n - 1]!, ...overflow]
  return { ...section, blocks }
}

export function setSectionColumns(section: LandingSection, columns: ColumnPreset): LandingSection {
  return fitColumns({ ...section, columns })
}

// ----------------------------------------------------------------- editing
export interface BlockAddress { section: number; column: number; index: number }

/** Move a block to another place (any section, any column). `to.index` is
 *  read against the destination column as it is BEFORE the block is removed. */
export function moveBlock(layout: LandingLayout, from: BlockAddress, to: BlockAddress): LandingLayout {
  const sections = layout.sections.map((s) => ({ ...s, blocks: s.blocks.map((c) => [...c]) }))
  const src = sections[from.section]?.blocks[from.column]
  const dst = sections[to.section]?.blocks[to.column]
  if (!src || !dst) return layout
  const [block] = src.splice(from.index, 1)
  if (!block) return layout
  let index = to.index
  if (from.section === to.section && from.column === to.column && from.index < to.index) index -= 1
  dst.splice(Math.max(0, Math.min(index, dst.length)), 0, block)
  return { ...layout, sections }
}

export function findBlock(layout: LandingLayout, id: string): (BlockAddress & { block: LandingBlock }) | null {
  for (let s = 0; s < layout.sections.length; s++) {
    const sec = layout.sections[s]!
    for (let c = 0; c < sec.blocks.length; c++) {
      const i = sec.blocks[c]!.findIndex((b) => b.id === id)
      if (i >= 0) return { section: s, column: c, index: i, block: sec.blocks[c]![i]! }
    }
  }
  return null
}

export function updateBlock(layout: LandingLayout, id: string, patch: (b: LandingBlock) => LandingBlock): LandingLayout {
  return {
    ...layout,
    sections: layout.sections.map((s) => ({ ...s, blocks: s.blocks.map((c) => c.map((b) => (b.id === id ? patch(b) : b))) })),
  }
}

export function removeBlock(layout: LandingLayout, id: string): LandingLayout {
  return { ...layout, sections: layout.sections.map((s) => ({ ...s, blocks: s.blocks.map((c) => c.filter((b) => b.id !== id)) })) }
}

export function insertBlock(layout: LandingLayout, block: LandingBlock, at: { section: number; column: number; index?: number }): LandingLayout {
  const sections = layout.sections.map((s, i) => (i !== at.section ? s : {
    ...s,
    blocks: s.blocks.map((c, j) => {
      if (j !== at.column) return c
      const next = [...c]
      next.splice(at.index ?? next.length, 0, block)
      return next
    }),
  }))
  return { ...layout, sections }
}

export function moveSection(layout: LandingLayout, index: number, delta: -1 | 1): LandingLayout {
  const j = index + delta
  if (j < 0 || j >= layout.sections.length) return layout
  const sections = [...layout.sections]
  ;[sections[index], sections[j]] = [sections[j]!, sections[index]!]
  return { ...layout, sections }
}

// ----------------------------------------------------------------- slugs
export function slugify(name: string): string {
  return name.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60).replace(/-+$/g, '') || 'page'
}

export function uniqueSlug(name: string, taken: Iterable<string>): string {
  const used = new Set(taken)
  const base = slugify(name)
  if (!used.has(base)) return base
  for (let n = 2; n < 1000; n++) {
    const s = `${base}-${n}`
    if (!used.has(s)) return s
  }
  return `${base}-${Math.random().toString(36).slice(2, 6)}`
}

export const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/

// ------------------------------------------------------------ publishing
/** The layout the editor works on: the draft, else the published layout. */
export function editingLayout(page: Pick<LandingPage, 'layout' | 'draft'>): LandingLayout {
  return page.draft ?? page.layout
}

export function sameLayout(a: LandingLayout, b: LandingLayout): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

export type PublishState = 'unpublished' | 'published' | 'changes'

export function publishState(page: Pick<LandingPage, 'publishedAt' | 'draft' | 'layout'>): PublishState {
  if (!page.publishedAt) return 'unpublished'
  return page.draft && !sameLayout(page.draft, page.layout) ? 'changes' : 'published'
}

// ------------------------------------------------------------------ forms
/** A form's family key: its source draft, else its own id (as reports store it). */
export function formFamilyOf(form: Pick<PublishedForm, 'id' | 'draftId'>): string {
  return form.draftId ?? form.id
}

/** The forms a block shows, from the live forms the viewer can open. Picked
 *  forms keep their order; a form the viewer cannot open is left out. */
export function resolveBlockForms(source: FormsSource, live: PublishedForm[], surfaceOf: (f: PublishedForm) => string): PublishedForm[] {
  if (source.mode === 'pick') {
    const byFamily = new Map(live.map((f) => [formFamilyOf(f), f]))
    return source.formFamilies.map((k) => byFamily.get(k)).filter((f): f is PublishedForm => !!f)
  }
  const pool = source.mode === 'surface' ? live.filter((f) => surfaceOf(f) === source.surface) : live
  return [...pool].sort((a, b) => formName(a).localeCompare(formName(b)))
}

export function formName(f: Pick<PublishedForm, 'name' | 'spec'>): string {
  return f.spec?.name || f.name
}

// ------------------------------------------------------------------ links
export function isSafeUrl(url: string): boolean {
  try {
    const u = new URL(url)
    return u.protocol === 'https:' || u.protocol === 'http:' || u.protocol === 'mailto:'
  } catch {
    return false
  }
}

export const PAGE_BASE = '/workflow-management/pages'

export function pagePath(slug: string): string {
  return `${PAGE_BASE}/${slug}`
}

/** Where a link goes: an in-app route, or an external URL. Null when it cannot go anywhere. */
export function linkDestination(target: LinkTarget, latestFormId: (family: string) => string | null): { internal: string } | { external: string; newTab: boolean } | null {
  switch (target.type) {
    case 'app': return target.path.startsWith('/') ? { internal: target.path } : null
    case 'page': return { internal: pagePath(target.slug) }
    case 'form': {
      const id = latestFormId(target.formFamily)
      return id ? { internal: `/workflow-management/forms/${id}/submit` } : null
    }
    case 'url': return isSafeUrl(target.url) ? { external: target.url, newTab: target.newTab !== false } : null
  }
}

// ----------------------------------------------------------------- search
export function matchesQuery(query: string, parts: Array<string | null | undefined>): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) return true
  const hay = parts.filter(Boolean).join(' ').toLowerCase()
  return words.every((w) => hay.includes(w))
}

// ------------------------------------------------------------- checklist
export interface LayoutProblem {
  blockId?: string
  sectionId?: string
  level: 'error' | 'warning'
  message: string
}

/** What the builder flags before publishing. Errors block Publish. */
export function layoutProblems(layout: LandingLayout, ctx: { liveFamilies: ReadonlySet<string>; pageSlugs: ReadonlySet<string> }): LayoutProblem[] {
  const out: LayoutProblem[] = []
  if (!layout.sections.some((s) => s.blocks.some((c) => c.length > 0))) {
    out.push({ level: 'error', message: 'The page is empty. Add at least one block.' })
  }
  for (const s of layout.sections) {
    if (s.blocks.every((c) => c.length === 0)) out.push({ sectionId: s.id, level: 'warning', message: `${s.title || 'A section'} has no blocks; it will not show.` })
    for (const b of s.blocks.flat()) {
      const name = b.title || blockKindInfo(b.kind).label
      if (b.kind === 'forms' && b.source.mode === 'pick') {
        if (b.source.formFamilies.length === 0) out.push({ blockId: b.id, level: 'error', message: `"${name}" has no forms picked.` })
        const gone = b.source.formFamilies.filter((f) => !ctx.liveFamilies.has(f)).length
        if (gone) out.push({ blockId: b.id, level: 'warning', message: `"${name}": ${gone} form${gone === 1 ? ' is' : 's are'} no longer published and will not show.` })
      }
      if (b.kind === 'links') {
        if (b.items.length === 0) out.push({ blockId: b.id, level: 'error', message: `"${name}" has no links.` })
        for (const i of b.items) {
          if (!i.label.trim()) out.push({ blockId: b.id, level: 'error', message: `"${name}" has a link with no label.` })
          if (i.target.type === 'url' && !isSafeUrl(i.target.url)) out.push({ blockId: b.id, level: 'error', message: `"${name}": "${i.label || 'a link'}" needs a full web address starting with https://.` })
          if (i.target.type === 'form' && !ctx.liveFamilies.has(i.target.formFamily)) out.push({ blockId: b.id, level: 'warning', message: `"${name}": the form behind "${i.label}" is not published.` })
          if (i.target.type === 'page' && !ctx.pageSlugs.has(i.target.slug)) out.push({ blockId: b.id, level: 'warning', message: `"${name}": the page behind "${i.label}" no longer exists or you cannot open it.` })
        }
      }
      if (b.kind === 'text' && !b.title?.trim() && !b.body.trim()) out.push({ blockId: b.id, level: 'error', message: 'A text block is empty.' })
    }
  }
  return out
}

/** Simple text formatting for Text blocks: paragraphs on blank lines, "- "
 *  bullets, **bold**, and [label](https://...) links. Returns plain parts the
 *  renderer turns into elements, so no HTML from the author is ever injected. */
export type TextPart = { kind: 'text'; text: string } | { kind: 'bold'; text: string } | { kind: 'link'; text: string; href: string }
export type TextChunk = { kind: 'p'; parts: TextPart[] } | { kind: 'ul'; items: TextPart[][] }

export function parseInline(line: string): TextPart[] {
  const parts: TextPart[] = []
  const re = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g
  let last = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(line))) {
    if (m.index > last) parts.push({ kind: 'text', text: line.slice(last, m.index) })
    if (m[1]) parts.push({ kind: 'bold', text: m[1] })
    else if (m[2] && m[3] && (m[3].startsWith('/') || isSafeUrl(m[3]))) parts.push({ kind: 'link', text: m[2], href: m[3] })
    else parts.push({ kind: 'text', text: m[0] })
    last = m.index + m[0].length
  }
  if (last < line.length) parts.push({ kind: 'text', text: line.slice(last) })
  return parts
}

export function parseText(body: string): TextChunk[] {
  const chunks: TextChunk[] = []
  for (const para of body.replace(/\r\n/g, '\n').split(/\n\s*\n/)) {
    const lines = para.split('\n').map((l) => l.trimEnd()).filter((l) => l.trim())
    if (!lines.length) continue
    if (lines.every((l) => /^\s*[-*]\s+/.test(l))) {
      chunks.push({ kind: 'ul', items: lines.map((l) => parseInline(l.replace(/^\s*[-*]\s+/, ''))) })
    } else {
      chunks.push({ kind: 'p', parts: parseInline(lines.join(' ')) })
    }
  }
  return chunks
}
